import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { logoutBeforeLoginIfNeeded } from '@/utils/logoutBeforeLogin';
import { ProfessionalsService } from '../professional.service';
import { removeProfessionalData, setProfessionalData } from '@/utils/cookies';

export const useCreateProfessional = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const createProfissionalMutation = useMutation({
		mutationFn: ({ body }: { body: Professionals.Create }) =>
			ProfessionalsService.create(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['professionals'] });
			toast.success('Profissional criado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na criação do profissional'),
			);
		},
	});

	return createProfissionalMutation;
};

export const useUpdateProfessional = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchProfissionalMutation = useMutation({
		mutationFn: ({
			body,
			id,
		}: {
			body: Partial<Professionals.Professional>;
			id: string;
		}) => ProfessionalsService.patch(body, id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['professionals'] });
			toast.success('Profissional atualizado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do profissional!'),
			);
		},
	});

	return patchProfissionalMutation;
};

export const useUpdateProfessionalAuthorization = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchProfissionalAuthorizationMutation = useMutation({
		mutationFn: ({
			body,
			id,
		}: {
			body: { authorized: boolean };
			id: string;
		}) => ProfessionalsService.pathAuthorization(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['professionals'] });
			toast.success('Alterado autorização com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do profissional!'),
			);
		},
	});

	return patchProfissionalAuthorizationMutation;
};

export const useUpdateProfessionalPermissionTags = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchPermissionTagsMutation = useMutation({
		mutationFn: ({
			body,
			id,
		}: {
			body: Professionals.UpdatePermissionTags;
			id: string;
		}) => ProfessionalsService.patchPermissionTags(id, body),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['professionals'] });
			queryClient.invalidateQueries({ queryKey: ['professional-me'] });

			const reconcileCount = data.video_reconcile_scheduled_count ?? 0;
			if (reconcileCount > 0) {
				queryClient.invalidateQueries({
					queryKey: ['sessions'],
					exact: false,
				});
				const sessionLabel =
					reconcileCount === 1 ? 'sessão futura' : 'sessões futuras';
				toast.success(
					`Tags atualizadas. ${reconcileCount} ${sessionLabel} serão reconciliadas em segundo plano.`,
				);
			} else {
				toast.success('Tags de permissão atualizadas com sucesso!');
			}

			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao atualizar tags de permissão!'),
			);
		},
	});

	return patchPermissionTagsMutation;
};

export const useUpdateProfessionalVisibility = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchVisibilityMutation = useMutation({
		mutationFn: ({
			body,
			id,
		}: {
			body: Professionals.UpdateVisibility;
			id: string;
		}) => ProfessionalsService.patchVisibility(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['professionals'] });
			queryClient.invalidateQueries({ queryKey: ['professional'] });
			toast.success('Visibilidade atualizada com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao atualizar visibilidade!'),
			);
		},
	});

	return patchVisibilityMutation;
};

/** Reordena a exibição dos profissionais visíveis ao usuário B2C (home e agendamento). */
export const useReorderProfessionalsDisplay = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const reorderMutation = useMutation({
		mutationFn: (body: Professionals.ReorderDisplay) =>
			ProfessionalsService.reorderDisplay(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['professionals'] });
			queryClient.invalidateQueries({ queryKey: ['professional'] });
			toast.success('Ordem de exibição salva!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao salvar a ordem de exibição!'),
			);
		},
	});

	return reorderMutation;
};

export const useLoginProfessional = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const loginProfessionalMutation = useMutation({
		mutationFn: async ({
			email,
			password,
		}: {
			email: string;
			password: string;
		}) => {
			await logoutBeforeLoginIfNeeded(queryClient);
			return ProfessionalsService.login({ email, password });
		},
		onSuccess: (data) => {
			toast.success('Login realizado com sucesso!');
			if (data.professional) {
				setProfessionalData(data.professional);
			}
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'E-mail/Senha inválidos.'));
		},
	});

	return loginProfessionalMutation;
};

export const useLogoutProfessional = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const logoutProfessionalMutation = useMutation({
		mutationFn: () => ProfessionalsService.logout(),
		onSuccess: () => {
			removeProfessionalData();

			queryClient.removeQueries({
				queryKey: ['professional-me'],
				exact: true,
			});

			onSuccess && onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro no logout.'));
		},
	});

	return logoutProfessionalMutation;
};
